import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Settings,
  UserPlus,
  Mail,
  ArrowLeft,
  Crown,
  Shield,
  Target,
  TrendingUp,
  Trash2,
  Edit2,
  LogOut,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  CheckCircle
} from 'lucide-react';
import { teamsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { calculateGoalProgress, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [team, setTeam] = useState(null);
  const [goals, setGoals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('goals');
  
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [expandedGoal, setExpandedGoal] = useState(null);
  
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    category: 'business',
    goal_type: 'strategic',
    key_results: [{ id: Date.now().toString(), title: '', target_value: 100, current_value: 0, unit: '%' }],
    due_date: '',
    assigned_to: [],
  });

  useEffect(() => {
    loadTeamData();
  }, [teamId]);

  const loadTeamData = async () => {
    try {
      const [teamRes, goalsRes, activityRes, statsRes] = await Promise.all([
        teamsApi.getOne(teamId),
        teamsApi.getGoals(teamId),
        teamsApi.getActivity(teamId),
        teamsApi.getStats(teamId),
      ]);
      setTeam(teamRes.data);
      setGoals(goalsRes.data || []);
      setActivities(activityRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load team:', error);
      toast.error('Failed to load team data');
      navigate('/teams');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = team?.members?.some(m => m.user_id === user?.id && m.role === 'admin');
  const isOwner = team?.owner_id === user?.id;

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await teamsApi.invite(teamId, inviteEmail);
      toast.success('Invitation sent!');
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    try {
      await teamsApi.leave(teamId);
      toast.success('Left the team');
      navigate('/teams');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave team');
    }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) return;
    try {
      await teamsApi.delete(teamId);
      toast.success('Team deleted');
      navigate('/teams');
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team?`)) return;
    try {
      await teamsApi.removeMember(teamId, memberId);
      setTeam(prev => ({
        ...prev,
        members: prev.members.filter(m => m.user_id !== memberId)
      }));
      toast.success('Member removed');
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await teamsApi.updateMemberRole(teamId, memberId, newRole);
      setTeam(prev => ({
        ...prev,
        members: prev.members.map(m => 
          m.user_id === memberId ? { ...m, role: newRole } : m
        )
      }));
      toast.success('Role updated');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      if (editingGoal) {
        // Update existing goal
        await teamsApi.updateGoal(teamId, editingGoal.id, goalFormData);
        setGoals(prev => prev.map(g => g.id === editingGoal.id ? { ...g, ...goalFormData } : g));
        toast.success('Goal updated!');
      } else {
        // Create new goal
        const response = await teamsApi.createGoal(teamId, goalFormData);
        setGoals(prev => [...prev, response.data]);
        toast.success('Goal created!');
      }
      setShowGoalForm(false);
      setEditingGoal(null);
      resetGoalForm();
      loadTeamData();
    } catch (error) {
      toast.error(editingGoal ? 'Failed to update goal' : 'Failed to create goal');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalFormData({
      title: goal.title || '',
      description: goal.description || '',
      category: goal.category || 'business',
      goal_type: goal.goal_type || 'strategic',
      key_results: goal.key_results?.length > 0 ? goal.key_results : [{ id: Date.now().toString(), title: '', target_value: 100, current_value: 0, unit: '%' }],
      due_date: goal.due_date || '',
      assigned_to: goal.assigned_to || [],
    });
    setShowGoalForm(true);
  };

  const resetGoalForm = () => {
    setGoalFormData({
      title: '',
      description: '',
      category: 'business',
      goal_type: 'strategic',
      key_results: [{ id: Date.now().toString(), title: '', target_value: 100, current_value: 0, unit: '%' }],
      due_date: '',
      assigned_to: [],
    });
    setEditingGoal(null);
  };

  const handleKeyResultUpdate = async (goalId, krId, value) => {
    try {
      await teamsApi.updateGoalKeyResult(teamId, goalId, krId, parseFloat(value));
      setGoals(prev => prev.map(g => {
        if (g.id === goalId) {
          return {
            ...g,
            key_results: g.key_results.map(kr => 
              kr.id === krId ? { ...kr, current_value: parseFloat(value) } : kr
            )
          };
        }
        return g;
      }));
    } catch (error) {
      toast.error('Failed to update key result');
    }
  };

  const handleGoalStatusChange = async (goalId, status) => {
    try {
      await teamsApi.updateGoalStatus(teamId, goalId, status);
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, status } : g));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await teamsApi.deleteGoal(teamId, goalId);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const addKeyResult = () => {
    setGoalFormData(prev => ({
      ...prev,
      key_results: [...prev.key_results, { 
        id: Date.now().toString(), 
        title: '', 
        target_value: 100, 
        current_value: 0, 
        unit: '%' 
      }]
    }));
  };

  const getActionText = (action) => {
    const actions = {
      'created_goal': 'created a goal',
      'updated_goal': 'updated a goal',
      'updated_progress': 'updated progress on',
      'marked_completed': 'completed',
      'marked_active': 'activated',
      'joined_team': 'joined the team',
    };
    return actions[action] || action;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  if (!team) return null;

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="team-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/teams')}
        className="inline-flex items-center gap-2 text-slate-400 hover:text-[#26C6DA] transition-colors"
        data-testid="back-to-teams-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Teams
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
                {team.name}
              </h1>
              {team.description && (
                <p className="text-white/80 max-w-xl">{team.description}</p>
              )}
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-white/60" />
                  <span className="text-white/80">{team.members?.length || 0} members</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-white/60" />
                  <span className="text-white/80">{stats?.total_goals || 0} goals</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              {isAdmin && (
                <Dialog open={showInviteForm} onOpenChange={setShowInviteForm}>
                  <DialogTrigger asChild>
                    <Button className="bg-white/20 hover:bg-white/30 text-white rounded-full gap-2" data-testid="invite-btn">
                      <UserPlus className="w-4 h-4" />
                      Invite
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Invite Team Member</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleInvite} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Email Address</Label>
                        <Input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="colleague@example.com"
                          required
                          className="bg-[#1A1A2E] border-[#1F2937]"
                          data-testid="invite-email-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setShowInviteForm(false)} className="rounded-full">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full" data-testid="send-invite-btn">
                          Send Invitation
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              {!isOwner && (
                <Button onClick={handleLeaveTeam} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 rounded-full gap-2" data-testid="leave-team-btn">
                  <LogOut className="w-4 h-4" />
                  Leave
                </Button>
              )}
              {isOwner && (
                <Button onClick={handleDeleteTeam} variant="ghost" className="text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-full gap-2" data-testid="delete-team-btn">
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              )}
            </div>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold font-mono">{stats.total_goals}</p>
                <p className="text-white/70 text-sm">Total Goals</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold font-mono">{stats.completed_goals}</p>
                <p className="text-white/70 text-sm">Completed</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold font-mono">{stats.average_progress}%</p>
                <p className="text-white/70 text-sm">Avg Progress</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold font-mono">{stats.completion_rate}%</p>
                <p className="text-white/70 text-sm">Completion Rate</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#111827] p-1 rounded-full">
          <TabsTrigger value="goals" className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white" data-testid="tab-goals">
            Goals
          </TabsTrigger>
          <TabsTrigger value="members" className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white" data-testid="tab-members">
            Members ({team.members?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white" data-testid="tab-activity">
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Goals Tab */}
        <TabsContent value="goals" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Dialog open={showGoalForm} onOpenChange={(open) => { setShowGoalForm(open); if (!open) resetGoalForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full px-6 gap-2" data-testid="create-team-goal-btn">
                  <Plus className="w-4 h-4" />
                  Add Team Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingGoal ? 'Edit Team Goal' : 'Create Team Goal'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateGoal} className="space-y-5 mt-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={goalFormData.title}
                      onChange={(e) => setGoalFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Launch Q1 Marketing Campaign"
                      required
                      className="bg-muted border-border"
                      data-testid="team-goal-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={goalFormData.description}
                      onChange={(e) => setGoalFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the objective..."
                      className="bg-muted border-border"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={goalFormData.category} onValueChange={(v) => setGoalFormData(prev => ({ ...prev, category: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="engineering">Engineering</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={goalFormData.due_date}
                        onChange={(e) => setGoalFormData(prev => ({ ...prev, due_date: e.target.value }))}
                        className="bg-muted border-border"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Key Results</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={addKeyResult} className="text-[#26C6DA]">
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    {goalFormData.key_results.map((kr, index) => (
                      <div key={kr.id} className="p-4 bg-muted/50 dark:bg-[#1A1A2E]/50 rounded-xl space-y-3">
                        <Input
                          value={kr.title}
                          onChange={(e) => {
                            const newKrs = [...goalFormData.key_results];
                            newKrs[index].title = e.target.value;
                            setGoalFormData(prev => ({ ...prev, key_results: newKrs }));
                          }}
                          placeholder="e.g., Achieve 10,000 website visits"
                          className="bg-background border-border"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            type="number"
                            value={kr.target_value}
                            onChange={(e) => {
                              const newKrs = [...goalFormData.key_results];
                              newKrs[index].target_value = parseFloat(e.target.value);
                              setGoalFormData(prev => ({ ...prev, key_results: newKrs }));
                            }}
                            placeholder="Target"
                            className="bg-background border-border"
                          />
                          <Input
                            value={kr.unit}
                            onChange={(e) => {
                              const newKrs = [...goalFormData.key_results];
                              newKrs[index].unit = e.target.value;
                              setGoalFormData(prev => ({ ...prev, key_results: newKrs }));
                            }}
                            placeholder="Unit"
                            className="bg-background border-border"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Assign Team Members */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#26C6DA]" />
                      Assign Team Members
                    </Label>
                    <div className="p-4 bg-muted/50 dark:bg-[#1A1A2E]/50 rounded-xl">
                      {team?.members?.length > 0 ? (
                        <div className="space-y-2">
                          {team.members.map((member) => (
                            <label 
                              key={member.user_id} 
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                goalFormData.assigned_to.includes(member.user_id) 
                                  ? 'bg-[#26C6DA]/10 border border-[#26C6DA]/30' 
                                  : 'hover:bg-muted border border-transparent'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={goalFormData.assigned_to.includes(member.user_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGoalFormData(prev => ({
                                      ...prev,
                                      assigned_to: [...prev.assigned_to, member.user_id]
                                    }));
                                  } else {
                                    setGoalFormData(prev => ({
                                      ...prev,
                                      assigned_to: prev.assigned_to.filter(id => id !== member.user_id)
                                    }));
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-[#26C6DA] focus:ring-[#26C6DA]"
                              />
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center text-white text-sm font-medium">
                                {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-800 dark:text-slate-100">{member.name || member.email}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{member.role}</p>
                              </div>
                              {goalFormData.assigned_to.includes(member.user_id) && (
                                <CheckCircle className="w-5 h-5 text-[#26C6DA]" />
                              )}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-2">
                          No team members yet. Invite members first.
                        </p>
                      )}
                    </div>
                    {goalFormData.assigned_to.length > 0 && (
                      <p className="text-xs text-[#26C6DA]">
                        {goalFormData.assigned_to.length} member{goalFormData.assigned_to.length > 1 ? 's' : ''} will be notified when this goal is {editingGoal ? 'updated' : 'created'}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowGoalForm(false)} className="rounded-full">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full" data-testid="save-team-goal-btn">
                      {editingGoal ? 'Update Goal' : 'Create Goal'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {goals.length > 0 ? (
            <div className="space-y-4">
              {goals.map((goal, index) => (
                <div key={goal.id} className="bg-card backdrop-blur-md border border-border rounded-2xl overflow-hidden" data-testid={`team-goal-${index}`}>
                  <div className="p-6 cursor-pointer" onClick={() => setExpandedGoal(expandedGoal === goal.id ? null : goal.id)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                            goal.status === 'completed' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                            goal.status === 'active' ? 'bg-[#26C6DA]/10 text-[#0891B2] dark:text-[#26C6DA]' :
                            'bg-muted text-muted-foreground'
                          }`}>{goal.status}</span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#26C6DA]/10 text-[#0891B2] dark:text-[#26C6DA] capitalize">{goal.category}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>{goal.title}</h3>
                        {goal.description && <p className="text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{goal.description}</p>}
                        {/* Assigned Members */}
                        {goal.assigned_to?.length > 0 && (
                          <div className="flex items-center gap-2 mt-3">
                            <span className="text-xs text-slate-500 dark:text-slate-400">Assigned to:</span>
                            <div className="flex -space-x-2">
                              {goal.assigned_to.slice(0, 4).map((userId) => {
                                const member = team?.members?.find(m => m.user_id === userId);
                                return member ? (
                                  <div 
                                    key={userId} 
                                    className="w-7 h-7 rounded-full bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center text-white text-xs font-medium border-2 border-white dark:border-[#111827]"
                                    title={member.name || member.email}
                                  >
                                    {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                                  </div>
                                ) : null;
                              })}
                              {goal.assigned_to.length > 4 && (
                                <div className="w-7 h-7 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-slate-700 dark:text-slate-200 text-xs font-medium border-2 border-white dark:border-[#111827]">
                                  +{goal.assigned_to.length - 4}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#0891B2] dark:text-[#26C6DA] font-mono">{calculateGoalProgress(goal.key_results)}%</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Progress</p>
                        </div>
                        {expandedGoal === goal.id ? <ChevronUp className="w-5 h-5 text-slate-500 dark:text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                      </div>
                    </div>
                    <Progress value={calculateGoalProgress(goal.key_results)} className="h-2 bg-muted mt-4" />
                  </div>

                  {expandedGoal === goal.id && (
                    <div className="px-6 pb-6 border-t border-border pt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-800 dark:text-slate-100">Key Results</span>
                        <div className="flex items-center gap-2">
                          <Select value={goal.status} onValueChange={(v) => handleGoalStatusChange(goal.id, v)}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" onClick={() => handleEditGoal(goal)} className="text-[#26C6DA]" data-testid={`edit-team-goal-${goal.id}-btn`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteGoal(goal.id)} className="text-red-500" data-testid={`delete-team-goal-${goal.id}-btn`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {goal.key_results?.map((kr, krIndex) => (
                        <div key={kr.id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-800 dark:text-slate-100">{kr.title || `Key Result ${krIndex + 1}`}</span>
                            <span className="text-sm text-slate-600 dark:text-slate-400">{kr.current_value} / {kr.target_value} {kr.unit}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <Progress value={kr.target_value > 0 ? (kr.current_value / kr.target_value) * 100 : 0} className="flex-1 h-2 bg-slate-200 dark:bg-muted" />
                            <Input
                              type="number"
                              value={kr.current_value}
                              onChange={(e) => handleKeyResultUpdate(goal.id, kr.id, e.target.value)}
                              className="w-24 bg-white dark:bg-background border-slate-300 dark:border-border text-center text-slate-800 dark:text-slate-100"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card backdrop-blur-md border border-border rounded-2xl p-12 text-center">
              <Target className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Team Goals Yet</h3>
              <p className="text-muted-foreground mb-6">Create a shared goal for your team to work towards together.</p>
            </div>
          )}
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-6">
          <div className="bg-card backdrop-blur-md border border-border rounded-2xl">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground">Team Members</h3>
            </div>
            <div className="divide-y divide-border">
              {team.members?.map((member, index) => (
                <div key={member.user_id} className="p-4 flex items-center justify-between" data-testid={`member-${index}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center text-white font-medium">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{member.name}</span>
                        {team.owner_id === member.user_id && <Crown className="w-4 h-4 text-amber-500" title="Owner" />}
                        {member.role === 'admin' && team.owner_id !== member.user_id && <Shield className="w-4 h-4 text-[#26C6DA]" title="Admin" />}
                      </div>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  {/* Show menu if admin AND (not yourself OR you're owner cleaning duplicates) */}
                  {isAdmin && (member.user_id !== user?.id || team.owner_id === user?.id) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {team.owner_id !== member.user_id && (
                          <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, member.role === 'admin' ? 'member' : 'admin')}>
                            {member.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleRemoveMember(member.user_id, member.name)} className="text-red-600">
                          {member.user_id === user?.id ? 'Remove Duplicate' : 'Remove from Team'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-6">
          <div className="bg-card backdrop-blur-md border border-border rounded-2xl">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground">Recent Activity</h3>
            </div>
            {activities.length > 0 ? (
              <div className="divide-y divide-border">
                {activities.map((activity, index) => (
                  <div key={activity.id} className="p-4 flex items-start gap-3" data-testid={`activity-${index}`}>
                    <div className="w-8 h-8 rounded-full bg-[#26C6DA]/10 flex items-center justify-center text-[#0891B2] dark:text-[#26C6DA] flex-shrink-0">
                      {activity.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-foreground">
                        <span className="font-medium">{activity.user_name}</span>
                        {' '}{getActionText(activity.action)}{' '}
                        <span className="font-medium">{activity.target_title}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDateTime(activity.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-muted-foreground">
                <p>No activity yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamDetail;
